import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import PdfViewer from "@/components/training/PdfViewer";
import PptSlides from "@/components/training/PptSlides";
import { format } from "date-fns";
import {
  GraduationCap, ArrowLeft, Plus, Users, ChartBar, CheckCircle, Warning, Clock, FileText, Certificate, Download
} from "@phosphor-icons/react";

export default function TrainingHome() {
  const navigate = useNavigate();
  const { user, isTrainingAdmin, canEditTraining } = useAuth();
  const [activeTab, setActiveTab] = useState(isTrainingAdmin ? "manage" : "my");
  const [trainings, setTrainings] = useState([]);
  const [myTrainings, setMyTrainings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [newTraining, setNewTraining] = useState({
    title: "", description: "", department: "", trainer_name: "",
    training_date: "", due_date: "",
    passing_score: 70, certificate_validity_months: 12,
    recording_url: "", allow_retakes: true,
    questions_per_attempt: 0, min_watch_percent: 0, regenerate_per_attempt: false,
  });
  const [creating, setCreating] = useState(false);

  // Employee materials dialog + certs list
  const [materialsTraining, setMaterialsTraining] = useState(null);
  const [materialsList, setMaterialsList] = useState([]);
  const [certificates, setCertificates] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [
        axios.get(`${API}/my/trainings`, { withCredentials: true }),
        axios.get(`${API}/my/certificates`, { withCredentials: true }),
      ];
      if (isTrainingAdmin) calls.push(axios.get(`${API}/training`, { withCredentials: true }));
      const res = await Promise.all(calls);
      setMyTrainings(res[0].data);
      setCertificates(res[1].data || []);
      if (isTrainingAdmin) setTrainings(res[2].data);
    } catch (err) {
      toast.error("Failed to load trainings");
    } finally {
      setLoading(false);
    }
  }, [isTrainingAdmin]);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBytes, setPreviewBytes] = useState(null);
  const [previewMat, setPreviewMat] = useState(null);
  const [previewText, setPreviewText] = useState(null);
  const [previewSlides, setPreviewSlides] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const resetPreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBytes(null);
    setPreviewMat(null);
    setPreviewText(null);
    setPreviewSlides(null);
    setPreviewError(null);
    setPreviewLoading(false);
  };

  const openMaterials = async (t) => {
    setMaterialsTraining(t);
    setMaterialsList([]);
    resetPreview();
    try {
      const res = await axios.get(`${API}/my/trainings/${t.training_id}/materials`, { withCredentials: true });
      const list = res.data || [];
      setMaterialsList(list);
      // Auto-preview the first previewable file (PDF, PPTX or text) so assignees land on a useful view
      const first = list.find((m) => ["pdf", "text", "pptx"].includes((m.file_type || "").toLowerCase()));
      if (first) {
        previewMaterial(first);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load materials");
    }
  };

  const previewMaterial = async (m) => {
    const ftype = (m.file_type || "").toLowerCase();
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBytes(null);
    setPreviewText(null);
    setPreviewSlides(null);
    setPreviewError(null);
    setPreviewMat(m);
    setPreviewLoading(true);
    try {
      if (ftype === "ppt" || ftype === "pptx") {
        // Built-in slide viewer: server extracts slide text + images from the pptx
        const res = await axios.get(`${API}/materials/${m.material_id}/slides`, { withCredentials: true });
        setPreviewSlides(res.data.slides || []);
      } else if (ftype === "text") {
        const res = await axios.get(`${API}/materials/${m.material_id}/stream`, { withCredentials: true, responseType: "text" });
        setPreviewText(typeof res.data === "string" ? res.data : String(res.data));
      } else {
        const res = await axios.get(`${API}/materials/${m.material_id}/stream`, { withCredentials: true, responseType: "blob" });
        const blobType = (res.data?.type || res.headers?.["content-type"] || "").toLowerCase();
        if (blobType.includes("text/plain")) {
          // Legacy file with no stored binary — backend sent its extracted text instead
          setPreviewText(await res.data.text());
        } else {
          // Hand raw bytes to PDF.js — no blob URL involved
          const buf = await res.data.arrayBuffer();
          setPreviewBytes(new Uint8Array(buf));
        }
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setPreviewError("unavailable");
      } else if (status === 403) {
        setPreviewError("forbidden");
      } else if (status === 400 && ftype === "ppt") {
        setPreviewError("legacy-ppt");
      } else {
        setPreviewError("generic");
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const createTraining = async () => {
    if (!newTraining.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/training`, newTraining, { withCredentials: true });
      toast.success("Training created. Add materials and generate questions next.");
      setCreateOpen(false);
      setNewTraining({
        title: "", description: "", department: "", trainer_name: "",
        training_date: "", due_date: "",
        passing_score: 70, certificate_validity_months: 12,
        recording_url: "", allow_retakes: true,
        questions_per_attempt: 0, min_watch_percent: 0, regenerate_per_attempt: false,
      });
      navigate(`/training/${res.data.training_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create training");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary" data-testid="training-loading">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="training-home">
      <header className="glass-header border-b border-black/10 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 md:px-6 h-16">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-to-home">
              <ArrowLeft size={18} className="mr-2" />
              Home
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-600 rounded-md flex items-center justify-center">
                <GraduationCap size={18} className="text-white" weight="bold" />
              </div>
              <div>
                <h1 className="text-lg font-bold">Training Intelligence</h1>
                <p className="text-xs text-muted-foreground">AI-powered learning & assessments</p>
              </div>
            </div>
          </div>
          <Badge variant="secondary">{user?.name}</Badge>
        </div>
      </header>

      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-secondary/50">
            <TabsTrigger value="my" data-testid="tab-my-trainings"><Users size={16} className="mr-2" /> My Trainings</TabsTrigger>
            {isTrainingAdmin && <TabsTrigger value="manage" data-testid="tab-manage"><ChartBar size={16} className="mr-2" /> Manage Trainings</TabsTrigger>}
          </TabsList>

          {/* My Trainings */}
          <TabsContent value="my">
            {/* Employee dashboard summary cards */}
            {myTrainings.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {(() => {
                  const total = myTrainings.length;
                  const passed = myTrainings.filter(t => t.latest_attempt?.result === "passed").length;
                  const failed = myTrainings.filter(t => t.latest_attempt?.result === "failed").length;
                  const inProgress = myTrainings.filter(t => t.latest_attempt && t.latest_attempt.status === "in_progress").length;
                  const pending = total - passed - failed - inProgress;
                  const cards = [
                    { label: "Assigned", value: total, cls: "" },
                    { label: "Passed", value: passed, cls: "text-green-600" },
                    { label: "Failed", value: failed, cls: "text-red-600" },
                    { label: "In Progress", value: inProgress, cls: "text-blue-600" },
                    { label: "Pending", value: pending, cls: "text-amber-600" },
                  ];
                  return cards.map((c) => (
                    <Card key={c.label} data-testid={`my-stat-${c.label.toLowerCase()}`}>
                      <CardContent className="pt-4">
                        <p className={`text-3xl font-bold ${c.cls}`}>{c.value}</p>
                        <p className="text-xs text-muted-foreground">{c.label}</p>
                      </CardContent>
                    </Card>
                  ));
                })()}
              </div>
            )}

            {myTrainings.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">
                <FileText size={40} className="mx-auto mb-3 opacity-50" />
                <p>No trainings assigned to you yet.</p>
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myTrainings.map((a) => {
                  const t = a.training;
                  const la = a.latest_attempt;
                  const passed = la?.result === "passed";
                  return (
                    <Card key={a.assignment_id} className="hover:shadow-md transition" data-testid={`my-training-${t.training_id}`}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-base">{t.title}</CardTitle>
                          {la ? (
                            <Badge className={passed ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                              {passed ? <CheckCircle size={12} className="mr-1" /> : <Warning size={12} className="mr-1" />}
                              {passed ? "Passed" : "Failed"} • {la.score}%
                            </Badge>
                          ) : (
                            <Badge variant="secondary"><Clock size={12} className="mr-1" /> Pending</Badge>
                          )}
                        </div>
                        <CardDescription className="line-clamp-2">{t.description || t.trainer_name}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between text-muted-foreground">
                          <span>Department</span><span>{t.department || "—"}</span>
                        </div>
                        <div className="flex justify-between text-muted-foreground">
                          <span>Due</span><span>{t.due_date || "—"}</span>
                        </div>
                        <div className="flex justify-between text-muted-foreground">
                          <span>Pass score</span><span>{t.passing_score}%</span>
                        </div>
                        <div className="flex flex-wrap gap-2 pt-2">
                          {isTrainingAdmin && (
                            <Button size="sm" variant="outline" onClick={() => navigate(`/training/${t.training_id}`)} data-testid={`view-training-${t.training_id}`}>
                              Manage
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => openMaterials(t)} data-testid={`materials-training-${t.training_id}`}>
                            <FileText size={14} className="mr-1" />
                            Materials
                          </Button>
                          {(!la || t.allow_retakes) && (
                            <Button size="sm" onClick={() => navigate(`/training/${t.training_id}/take`)} data-testid={`take-training-${t.training_id}`}>
                              {la ? (passed ? "Retake to Improve" : "Retake") : "Take Assessment"}
                            </Button>
                          )}
                          {la && (
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/training/result/${la.attempt_id}`)}>
                              Result
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* My Certificates */}
            {certificates.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2"><Certificate size={20} /> My Certificates</h3>
                <Card>
                  <CardContent className="pt-4 divide-y">
                    {certificates.map((c) => (
                      <div key={c.certificate_id} className="flex items-center justify-between py-2" data-testid={`cert-row-${c.certificate_id}`}>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{c.training_title}</p>
                          <p className="text-xs text-muted-foreground">
                            Score {c.score}% • Issued {c.issued_date?.split("T")[0]} • Valid till {c.expiry_date?.split("T")[0]}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/certificate/${c.certificate_id}`)} data-testid={`download-cert-${c.certificate_id}`}>
                          <Download size={14} className="mr-1" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Manage */}
          {isTrainingAdmin && (
            <TabsContent value="manage">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">All Trainings</h2>
                {canEditTraining && (
                  <Button onClick={() => setCreateOpen(true)} data-testid="create-training-btn">
                    <Plus size={16} className="mr-2" />
                    Create Training
                  </Button>
                )}
              </div>
              {trainings.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">
                  No trainings created yet. Click "Create Training" to begin.
                </CardContent></Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {trainings.map((t) => (
                    <Card key={t.training_id} className="cursor-pointer hover:shadow-md" onClick={() => navigate(`/training/${t.training_id}`)} data-testid={`training-card-${t.training_id}`}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-base">{t.title}</CardTitle>
                          <Badge variant={t.status === "published" ? "default" : "secondary"}>{t.status}</Badge>
                        </div>
                        <CardDescription className="line-clamp-2">{t.description || "—"}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-1 text-sm text-muted-foreground">
                        <div className="flex justify-between"><span>Trainer</span><span className="text-foreground">{t.trainer_name || "—"}</span></div>
                        <div className="flex justify-between"><span>Department</span><span className="text-foreground">{t.department || "—"}</span></div>
                        <div className="flex justify-between"><span>Date</span><span className="text-foreground">{t.training_date || "—"}</span></div>
                        <div className="flex justify-between"><span>Pass score</span><span className="text-foreground">{t.passing_score}%</span></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="create-training-dialog">
          <DialogHeader>
            <DialogTitle>Create Training Session</DialogTitle>
            <DialogDescription>Fill in the basics — you can add materials, generate AI questions and assign employees next.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input value={newTraining.title} onChange={(e) => setNewTraining({ ...newTraining, title: e.target.value })} placeholder="e.g. ISO 9001 Quality Awareness" data-testid="new-training-title" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={newTraining.description} onChange={(e) => setNewTraining({ ...newTraining, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Department</Label>
                <Input value={newTraining.department} onChange={(e) => setNewTraining({ ...newTraining, department: e.target.value })} placeholder="e.g. Production" />
              </div>
              <div>
                <Label>Trainer</Label>
                <Input value={newTraining.trainer_name} onChange={(e) => setNewTraining({ ...newTraining, trainer_name: e.target.value })} />
              </div>
              <div>
                <Label>Training Date</Label>
                <Input type="date" value={newTraining.training_date} onChange={(e) => setNewTraining({ ...newTraining, training_date: e.target.value })} />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={newTraining.due_date} onChange={(e) => setNewTraining({ ...newTraining, due_date: e.target.value })} />
              </div>
              <div>
                <Label>Passing Score (%)</Label>
                <Input type="number" min={0} max={100} value={newTraining.passing_score} onChange={(e) => setNewTraining({ ...newTraining, passing_score: parseInt(e.target.value) || 70 })} />
              </div>
              <div>
                <Label>Certificate Validity (months)</Label>
                <Input type="number" min={1} value={newTraining.certificate_validity_months} onChange={(e) => setNewTraining({ ...newTraining, certificate_validity_months: parseInt(e.target.value) || 12 })} />
              </div>
              <div>
                <Label>Questions per Attempt <span className="text-xs text-muted-foreground">(0 = all)</span></Label>
                <Input type="number" min={0} value={newTraining.questions_per_attempt} onChange={(e) => setNewTraining({ ...newTraining, questions_per_attempt: parseInt(e.target.value) || 0 })} placeholder="0 (use all)" data-testid="new-training-qpa" />
                <p className="text-xs text-muted-foreground mt-1">Useful for retakes — build a larger question bank, serve a random subset each attempt.</p>
              </div>
              <div>
                <Label>Min. Video Watch % <span className="text-xs text-muted-foreground">(0 = no gate)</span></Label>
                <Input type="number" min={0} max={100} value={newTraining.min_watch_percent} onChange={(e) => setNewTraining({ ...newTraining, min_watch_percent: Math.min(100, parseInt(e.target.value) || 0) })} placeholder="0" data-testid="new-training-min-watch" />
                <p className="text-xs text-muted-foreground mt-1">Trainee must watch this % before unlocking the assessment.</p>
              </div>
            </div>
            <div className="flex items-start justify-between p-3 border rounded-md bg-secondary/20">
              <div className="flex-1">
                <Label className="text-sm font-medium">AI regenerates fresh questions every attempt</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Each attempt invokes GPT-5.2 to draft a brand-new question set from the source material. Best for ensuring trainees can't memorise the bank.
                </p>
              </div>
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={newTraining.regenerate_per_attempt}
                onChange={(e) => setNewTraining({ ...newTraining, regenerate_per_attempt: e.target.checked })}
                data-testid="new-training-regen"
              />
            </div>
            <div>
              <Label>Recording URL (Google Drive / YouTube)</Label>
              <Input value={newTraining.recording_url} onChange={(e) => setNewTraining({ ...newTraining, recording_url: e.target.value })} placeholder="https://drive.google.com/..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createTraining} disabled={creating} data-testid="confirm-create-training">
              {creating ? "Creating..." : "Create Training"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Materials preview dialog (employee) */}
      <Dialog open={!!materialsTraining} onOpenChange={(o) => {
        if (!o) {
          setMaterialsTraining(null);
          resetPreview();
        }
      }}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="materials-dialog">
          <DialogHeader>
            <DialogTitle>Study Materials</DialogTitle>
            <DialogDescription>{materialsTraining?.title}</DialogDescription>
          </DialogHeader>

          {materialsList.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">No study materials for this training yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 overflow-hidden flex-1 min-h-[60vh]">
              {/* Material list */}
              <div className="border rounded-md overflow-y-auto">
                <div className="divide-y">
                  {materialsList.map((m) => {
                    const active = previewMat?.material_id === m.material_id;
                    return (
                      <button
                        key={m.material_id}
                        type="button"
                        onClick={() => previewMaterial(m)}
                        className={`w-full text-left px-3 py-2 hover:bg-accent transition ${active ? "bg-accent" : ""}`}
                        data-testid={`preview-material-${m.material_id}`}
                      >
                        <p className="font-medium text-sm truncate">{m.file_name}</p>
                        <p className="text-xs text-muted-foreground uppercase">{m.file_type} • {Math.round((m.size_bytes || 0) / 1024)} KB</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Preview pane */}
              <div
                className="border rounded-md bg-muted/40 relative overflow-hidden"
                onContextMenu={(e) => e.preventDefault()}
                data-testid="material-preview-pane"
              >
                {!previewMat ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6 text-center">
                    Pick a file on the left to start studying.
                  </div>
                ) : previewLoading ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6 text-center" data-testid="preview-loading">
                    Loading preview…
                  </div>
                ) : previewError === "legacy-ppt" ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6 text-center" data-testid="preview-legacy-ppt">
                    This is a legacy .ppt file which can&apos;t be rendered in-app. Please ask your training admin to save it as .pptx or PDF and re-upload.
                  </div>
                ) : previewError === "unavailable" ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6 text-center" data-testid="preview-unavailable">
                    This file was uploaded before in-app previews existed, so its contents can&apos;t be shown here. Please ask your training admin to re-upload it as a PDF.
                  </div>
                ) : previewError === "forbidden" ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6 text-center" data-testid="preview-forbidden">
                    You don&apos;t have access to this material.
                  </div>
                ) : previewError ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6 text-center" data-testid="preview-error">
                    Couldn&apos;t load the preview. Please try again.
                  </div>
                ) : previewSlides !== null ? (
                  <PptSlides slides={previewSlides} fileName={previewMat.file_name} />
                ) : previewText !== null ? (
                  <ScrollArea className="h-full" data-testid="preview-text-scroll">
                    <pre className="p-4 text-sm whitespace-pre-wrap font-sans leading-relaxed" data-testid="preview-text">{previewText}</pre>
                  </ScrollArea>
                ) : previewBytes ? (
                  <PdfViewer data={previewBytes} />
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6 text-center">
                    This file type cannot be previewed in the browser.
                  </div>
                )}
              </div>
            </div>
          )}

          {materialsTraining?.recording_url && (
            <a href={materialsTraining.recording_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-2">
              Open training recording in new tab →
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
